import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { AvatarMedium, NamedLink } from '../../components';
import {
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
} from '../../util/fieldHelpers';
import { richText } from '../../util/richText';
import {
  formatExpiryCountdown,
  formatExpiryDateTime,
  isExpiryInPast,
  isListingExpired,
} from '../../util/listingExpiry';
import {
  LISTING_STATE_CLOSED,
  LISTING_STATE_PENDING_APPROVAL,
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
} from '../../util/types';
import { resolveLatestProcessName } from '../../transactions/transaction';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 16;
const QUANTITY_FIELD_PATTERN = /\b(qty|quantity|quantities|required|requested|needed|amount)\b/i;
const UNIT_FIELD_PATTERN = /\b(unit|uom|measure|package|packaging|container|bottle|bottles|vial|vials|box|boxes|case|cases|pack|packs|piece|pieces)\b/i;

const normalizeFieldConfigText = value => {
  return `${value || ''}`.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[-_]/g, ' ');
};

const getFieldConfigText = config => {
  return [
    config?.key,
    config?.label,
    config?.showConfig?.label,
    config?.saveConfig?.label,
    config?.filterConfig?.label,
  ]
    .filter(Boolean)
    .join(' ');
};

const getFirstDefinedValue = (data, keys) => {
  return keys.reduce((selected, key) => {
    return selected != null && selected !== '' ? selected : data?.[key];
  }, null);
};

const humanizeUnit = value => {
  return value && typeof value === 'string' ? value.replace(/[-_]/g, ' ') : null;
};

const getFieldValueFromListingData = (publicData, metadata, config) => {
  const { key, scope = 'public' } = config;
  const source = scope === 'metadata' ? metadata : publicData;
  const value = source?.[key];

  return value != null && value !== '' ? value : null;
};

const getFieldOptionLabel = (value, enumOptions = []) => {
  const optionConfig = enumOptions.find(o => `${o.option}` === `${value}`);
  return optionConfig?.label || value;
};

const formatListingFieldValue = (value, config) => {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return null;
  }

  const { schemaType, enumOptions } = config;

  return schemaType === SCHEMA_TYPE_ENUM
    ? `${getFieldOptionLabel(value, enumOptions)}`
    : schemaType === SCHEMA_TYPE_MULTI_ENUM
    ? value.map(v => getFieldOptionLabel(v, enumOptions)).join(', ')
    : schemaType === SCHEMA_TYPE_LONG || schemaType === SCHEMA_TYPE_TEXT
    ? `${value}`
    : null;
};

const getListingFieldValueLabel = (publicData, metadata, config) => {
  const value = getFieldValueFromListingData(publicData, metadata, config);
  return formatListingFieldValue(value, config);
};

const getSelectedListingCategories = (publicData = {}, categoryConfiguration = {}) => {
  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;

  if (!categoryPrefix || !listingCategoriesConfig) {
    return [];
  }

  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  return Object.values(categoriesObj);
};

const isListingFieldForCard = (config, publicData, selectedCategories) => {
  const listingType = publicData?.listingType;
  return (
    isFieldForListingType(listingType, config) && isFieldForCategory(selectedCategories, config)
  );
};

const formatQuantityLabel = (quantity, unit, sourceText = '') => {
  const quantityString = quantity != null && quantity !== '' ? `${quantity}`.trim() : null;
  const unitString = unit != null && unit !== '' ? `${unit}`.trim() : null;
  const quantityAlreadyHasUnit = unitString
    ? quantityString?.toLowerCase().includes(unitString.toLowerCase())
    : false;
  const sourceLooksUnitSpecific = UNIT_FIELD_PATTERN.test(sourceText);

  return quantityString && unitString && !quantityAlreadyHasUnit && !sourceLooksUnitSpecific
    ? `${quantityString} ${unitString}`
    : quantityString;
};

const getConfiguredQuantityLabel = (
  publicData,
  metadata,
  listingFieldConfigs,
  categoryConfiguration
) => {
  const selectedCategories = getSelectedListingCategories(publicData, categoryConfiguration);
  const availableFieldConfigs = (listingFieldConfigs || []).filter(config => {
    const value = getFieldValueFromListingData(publicData, metadata, config);
    return value != null && isListingFieldForCard(config, publicData, selectedCategories);
  });
  const quantityFieldConfig = availableFieldConfigs.find(config =>
    QUANTITY_FIELD_PATTERN.test(normalizeFieldConfigText(getFieldConfigText(config)))
  );

  if (!quantityFieldConfig) {
    return null;
  }

  const quantityLabel = getListingFieldValueLabel(publicData, metadata, quantityFieldConfig);
  const quantityFieldText = normalizeFieldConfigText(getFieldConfigText(quantityFieldConfig));
  const unitFieldConfig = availableFieldConfigs.find(config => {
    const fieldText = normalizeFieldConfigText(getFieldConfigText(config));
    return config.key !== quantityFieldConfig.key && UNIT_FIELD_PATTERN.test(fieldText);
  });
  const unitLabel = unitFieldConfig
    ? getListingFieldValueLabel(publicData, metadata, unitFieldConfig)
    : null;

  return formatQuantityLabel(quantityLabel, unitLabel, quantityFieldText);
};

const getDirectQuantityLabel = (publicData = {}, metadata = {}) => {
  const listingData = { ...metadata, ...publicData };
  const quantity = getFirstDefinedValue(listingData, [
    'quantity',
    'qty',
    'requestedQuantity',
    'requestQuantity',
    'requiredQuantity',
    'neededQuantity',
    'quantityNeeded',
    'quantityRequired',
    'amount',
  ]);
  const unit = humanizeUnit(
    getFirstDefinedValue(listingData, [
      'quantityUnit',
      'requestedQuantityUnit',
      'requestQuantityUnit',
      'unit',
      'unitLabel',
      'uom',
      'package',
      'packaging',
      'container',
      'containerType',
    ])
  );

  return formatQuantityLabel(quantity, unit);
};

const findCategory = (categories = [], categoryId) => {
  return categories.reduce((found, category) => {
    if (found) {
      return found;
    }
    if (category.id === categoryId) {
      return category;
    }
    return findCategory(category.subcategories || [], categoryId);
  }, null);
};

const getCategoryLabel = (publicData = {}, categoryConfiguration = {}) => {
  const selectedCategories = getSelectedListingCategories(publicData, categoryConfiguration);
  const selectedCategoryId = selectedCategories[selectedCategories.length - 1];
  const categoryConfig = findCategory(categoryConfiguration.categories, selectedCategoryId);

  return categoryConfig?.label || categoryConfig?.name || selectedCategoryId || null;
};

const getListingStatusLabel = listing => {
  const state = listing?.attributes?.state;
  return isListingExpired(listing)
    ? 'Expired'
    : state === LISTING_STATE_CLOSED
    ? 'Closed'
    : state === LISTING_STATE_PENDING_APPROVAL
    ? 'Pending approval'
    : 'Open for offers';
};

const getListingTypeBadge = (isBuyerRequest, foundListingTypeConfig, hasCurrentUserBid) => {
  return hasCurrentUserBid
    ? 'Bid already placed'
    : isBuyerRequest
    ? 'Buyer Request'
    : foundListingTypeConfig?.label || 'Listing';
};

const getRequestTypeLabel = (isBuyerRequest, foundListingTypeConfig) => {
  return isBuyerRequest
    ? 'Supplier offer requested'
    : foundListingTypeConfig?.label || 'Listing request';
};

export const getOpposhopListingDisplayData = props => {
  const {
    listing,
    listingFieldConfigs,
    categoryConfiguration,
    foundListingTypeConfig,
    isBuyerRequest,
    hasCurrentUserBid: hasCurrentUserBidProp,
  } = props;
  const { publicData = {}, metadata = {} } = listing.attributes;
  const hasCurrentUserBid =
    isBuyerRequest &&
    (hasCurrentUserBidProp === true ||
      metadata?.hasCurrentUserBid === true ||
      metadata?.hasProviderSubmittedOffer === true);
  const stockQuantity = listing.currentStock?.attributes?.quantity;
  const quantity =
    getConfiguredQuantityLabel(publicData, metadata, listingFieldConfigs, categoryConfiguration) ||
    getDirectQuantityLabel(publicData, metadata) ||
    formatQuantityLabel(stockQuantity);
  const category = getCategoryLabel(publicData, categoryConfiguration);
  const status = getListingStatusLabel(listing);
  const expiresAt = publicData?.expiresAt || null;
  const isExpired = isListingExpired(listing);

  return {
    quantity,
    category,
    requestType: getRequestTypeLabel(isBuyerRequest, foundListingTypeConfig),
    status,
    expiresAt,
    expiryDateTime: formatExpiryDateTime(expiresAt),
    isExpired,
    hasCurrentUserBid,
    badge: getListingTypeBadge(isBuyerRequest, foundListingTypeConfig, hasCurrentUserBid),
  };
};

const useExpiryNow = expiresAt => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!expiresAt) {
      return undefined;
    }

    const timer = setInterval(() => {
      setNow(Date.now());
    }, 60 * 1000);

    return () => {
      clearInterval(timer);
    };
  }, [expiresAt]);

  return now;
};

const ExpiryValue = props => {
  const { expiresAt, now } = props;
  const countdown = formatExpiryCountdown(expiresAt, now);
  const expiryDateTime = formatExpiryDateTime(expiresAt);

  return countdown || expiryDateTime ? (
    <span className={css.oppoExpiryValue}>
      {countdown ? <span>{countdown}</span> : null}
      {expiryDateTime ? <small>{expiryDateTime}</small> : null}
    </span>
  ) : null;
};

const DetailIcon = props => {
  const { label } = props;
  return (
    <span className={css.oppoDetailIcon} aria-hidden="true">
      {label}
    </span>
  );
};

export const ListingBreadcrumbBar = props => {
  const { title, isBuyerRequest } = props;

  return (
    <div className={css.oppoBreadcrumbBar}>
      <div className={css.oppoBreadcrumbInner}>
        <nav className={css.oppoBreadcrumbTrail} aria-label="Breadcrumb">
          <NamedLink className={css.oppoBreadcrumbLink} name="SearchPage">
            Browse Listings
          </NamedLink>
          <span className={css.oppoBreadcrumbSeparator}>/</span>
          <span className={css.oppoBreadcrumbCurrent}>{title}</span>
        </nav>
      </div>
    </div>
  );
};

const AuthorProfileLink = ({ author, children }) =>
  author?.id?.uuid ? (
    <NamedLink className={css.oppoAuthorLink} name="ProfilePage" params={{ id: author.id.uuid }}>
      {children}
    </NamedLink>
  ) : (
    <>{children}</>
  );

export const ListingSummaryCard = props => {
  const { title, author, authorDisplayName, displayData } = props;
  const now = useExpiryNow(displayData.expiresAt);
  const isExpired = displayData.isExpired || isExpiryInPast(displayData.expiresAt, now);
  const status = isExpired ? 'Expired' : displayData.status;
  const richTitle = richText(title, {
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });
  const rows = [
    { key: 'quantity', label: 'Total Quantity', value: displayData.quantity, icon: 'Q' },
    { key: 'category', label: 'Category', value: displayData.category, icon: 'C' },
    { key: 'requestType', label: 'Request Type', value: displayData.requestType, icon: 'R' },
    {
      key: 'expiry',
      label: 'Expires',
      value: displayData.expiresAt ? (
        <ExpiryValue expiresAt={displayData.expiresAt} now={now} />
      ) : null,
      icon: 'E',
    },
    { key: 'status', label: 'Status', value: status, icon: 'S' },
  ].filter(row => row.value != null && row.value !== '');

  return (
    <section className={css.oppoSummaryCard}>
      <div className={css.oppoSummaryHero}>
        <div className={css.oppoSummaryIcon} aria-hidden="true">
          <span />
        </div>
        <div className={css.oppoSummaryHeading}>
          <div className={css.oppoHeroBadges}>
            <span
              className={classNames(css.oppoTypeBadge, {
                [css.oppoTypeBadgeSuccess]: displayData.hasCurrentUserBid,
              })}
            >
              {displayData.badge}
            </span>
            <span
              className={classNames(css.oppoStatusBadge, {
                [css.oppoStatusBadgeExpired]: isExpired,
              })}
            >
              <span />
              {status}
            </span>
          </div>
          <h1>{richTitle}</h1>
          <p>
            <AuthorProfileLink author={author}>Posted by {authorDisplayName}</AuthorProfileLink>
          </p>
        </div>
      </div>
      <ul className={css.oppoAttributeList}>
        {rows.map(row => (
          <li key={row.key} className={css.oppoAttributeRow}>
            <DetailIcon label={row.icon} />
            <div>
              <span>{row.label}</span>
              <strong>{row.value}</strong>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export const RequestDetailsCard = props => {
  const { description, isBuyerRequest } = props;
  const content = richText(description || 'No additional request details provided.', {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
    breakChars: '/',
  });

  return (
    <section className={css.oppoContentCard}>
      <h2>{isBuyerRequest ? 'Request Details' : 'Listing Details'}</h2>
      <p className={css.oppoRequestText}>{content}</p>
      {isBuyerRequest ? (
        <div className={css.oppoInfoNotice}>
          <span aria-hidden="true">i</span>
          <p>
            This is a buyer request, not a product listing. Suppliers browse these requests and
            submit competitive offers directly to the buyer.
          </p>
        </div>
      ) : null}
    </section>
  );
};

const StatusDetailRow = props => {
  const { icon, label } = props;
  return (
    <li>
      <DetailIcon label={icon} />
      <span>{label}</span>
    </li>
  );
};

export const ListingSidebarCards = props => {
  const {
    orderPanel,
    author,
    authorDisplayName,
    displayData,
    isBuyerRequest,
    isOwnListing,
    onContactUser,
  } = props;
  const now = useExpiryNow(displayData.expiresAt);
  const isExpired = displayData.isExpired || isExpiryInPast(displayData.expiresAt, now);
  const hasCurrentUserBid = displayData.hasCurrentUserBid === true;
  const orderPanelDisabled = isExpired || hasCurrentUserBid;
  const disabledOrderPanelLabel = isExpired ? 'Expired' : 'Offer Submitted';
  const disabledOrderPanelTitle = isExpired
    ? 'This request has expired.'
    : 'You have already submitted an offer.';
  const showContact = !!author?.id && !isOwnListing;
  const contactLabel = isBuyerRequest ? 'Contact buyer' : 'Contact seller';
  const helperText =
    isExpired || hasCurrentUserBid
      ? null
      : isBuyerRequest
      ? 'Suppliers can submit offers based on this buyer request.'
      : 'Use the action above to proceed with this listing.';
  const statusRows = [
    {
      key: 'status',
      icon: 'S',
      label: isExpired ? 'Expired request' : isBuyerRequest ? 'Open request' : displayData.status,
    },
    displayData.expiresAt
      ? {
          key: 'expiry',
          icon: 'E',
          label: <ExpiryValue expiresAt={displayData.expiresAt} now={now} />,
        }
      : null,
    displayData.quantity
      ? { key: 'quantity', icon: 'Q', label: `Quantity: ${displayData.quantity}` }
      : null,
    hasCurrentUserBid
      ? { key: 'offer', icon: 'B', label: 'Bid already placed' }
      : isBuyerRequest
      ? { key: 'offer', icon: 'O', label: 'Offer required to proceed' }
      : null,
    displayData.category
      ? { key: 'category', icon: 'C', label: `Category: ${displayData.category}` }
      : null,
  ].filter(Boolean);

  return (
    <aside className={css.oppoSidebar}>
      <section className={css.oppoSidebarCard}>
        <div className={css.oppoSidebarAuthor}>
          <AvatarMedium user={author} disableProfileLink={!author?.id?.uuid} />
          <div>
            <AuthorProfileLink author={author}>
              <span>Posted by</span>
              <strong>{authorDisplayName}</strong>
            </AuthorProfileLink>
          </div>
        </div>
        <div className={css.oppoSidebarOrderPanel}>
          {orderPanelDisabled ? (
            <div
              className={classNames(css.oppoExpiredButton, {
                [css.oppoSubmittedOfferButton]: hasCurrentUserBid,
              })}
              title={disabledOrderPanelTitle}
              aria-disabled="true"
            >
              {disabledOrderPanelLabel}
            </div>
          ) : (
            orderPanel
          )}
        </div>
        {isExpired ? <p className={css.oppoExpiredNotice}>This request has expired.</p> : null}
        {!isExpired && hasCurrentUserBid ? (
          <p className={css.oppoSubmittedOfferNotice}>
            You have already submitted an offer for this request.
          </p>
        ) : null}
        {showContact ? (
          <button
            className={css.oppoContactButton}
            type="button"
            onClick={() => onContactUser(author)}
          >
            {contactLabel}
          </button>
        ) : null}
        {helperText ? <p className={css.oppoSidebarHelper}>{helperText}</p> : null}
      </section>

      <section className={classNames(css.oppoSidebarCard, css.oppoStatusCard)}>
        <ul>
          {statusRows.map(({ key, ...row }) => (
            <StatusDetailRow key={key} {...row} />
          ))}
        </ul>
      </section>

      <section className={classNames(css.oppoSidebarCard, css.oppoHowItWorks)}>
        <h2>How it works</h2>
        <ol>
          <li>
            <span>1</span>
            Browse buyer requests
          </li>
          <li>
            <span>2</span>
            Review the request details
          </li>
          <li>
            <span>3</span>
            Submit your best offer
          </li>
          <li>
            <span>4</span>
            Buyer reviews and accepts
          </li>
        </ol>
      </section>
    </aside>
  );
};
